import type { opensearchtypes } from '@opensearch-project/opensearch';
import { isNil } from 'lodash';
import { abortControllerService } from '@logz-pkg/utils';
import { IBuildSearchPayload } from '@logz-pkg/opensearch-dsl';
import { CrudApiService } from '../utilities/crud.api.service';
import { ICrudApiService } from '../utilities/types';
import { handleDalError } from '../utilities/utilities';
import {
  getOpensearchErrorMessage,
  isOpensearchErrorResponse,
  toOpensearchSearchError,
} from './opensearch-search-error';
import { opensearchQueryService } from './opensearch-query.service';

export type QueryMarkers = { n: string; source?: 'explore' | 'dashboards'; warm?: boolean };

const queryMarkersToString = (queryMarkers?: QueryMarkers): string => {
  if (!queryMarkers) return '';

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(queryMarkers)) {
    if (value === undefined || value === null) continue;

    params.append(key, String(value));
  }

  const qs = params.toString();

  return qs ? `?${qs}` : '';
};

type SearchLogsOptions = {
  abortKey?: string;
  signal?: AbortSignal;
};

export class OpensearchApiService implements ICrudApiService<opensearchtypes.SearchResponse> {
  crudApiService = new CrudApiService<opensearchtypes.SearchResponse>();

  urls = {
    search: '/osd-2-0/internal/search/opensearch',
    scrollSearch: '/osd-2-0/api/opensearch-dashboards',
  };

  searchLogs = async <T = any>(
    params: IBuildSearchPayload,
    queryMarkers?: QueryMarkers,
    { abortKey, signal }: SearchLogsOptions = {},
  ): Promise<opensearchtypes.SearchResponse<T>> => {
    const payload = opensearchQueryService.buildSearchPayload(params);
    const query = queryMarkersToString(queryMarkers);

    let abortSignal: AbortSignal | undefined = signal;

    if (!abortSignal && !isNil(abortKey)) {
      abortControllerService.abort(abortKey);

      const abortController = abortControllerService.create(abortKey);

      abortController.signal.onabort = () => {
        abortControllerService.delete(abortKey);
      };

      abortSignal = abortController.signal;
    }

    try {
      const response = await this.crudApiService.do<{ rawResponse: opensearchtypes.SearchResponse<T> }>(
        queryMarkers ? `${this.urls.search}${query}` : this.urls.search,
        {
          payload,
          abortSignal,
        },
      );

      // The search endpoint answers 200 and reports the OpenSearch failure inside the envelope.
      const searchError = toOpensearchSearchError(response);

      if (searchError) throw searchError;

      return response?.rawResponse;
    } catch (e) {
      if (e?.config?.signal?.aborted) throw e;

      // Converted before it is rethrown: panel alerts and tooltips render `Error.message`, and the value
      // a rejected search carries is a bare `{ error }` body with no message of its own.
      handleDalError(toOpensearchSearchError(e) ?? e);
    }
  };

  getLogById = async (
    id: string,
    queryMarkers?: QueryMarkers,
    options: SearchLogsOptions = {},
  ): Promise<opensearchtypes.SearchHit | undefined> => {
    const response = await this.searchLogs(
      {
        query: { ids: { values: [id] } },
      },
      queryMarkers,
      options,
    );

    return response?.hits?.hits?.[0];
  };

  getErrorMessage = (errorOrErrorResponse: Error | opensearchtypes.ErrorResponseBase | unknown): string | undefined => {
    if (errorOrErrorResponse instanceof Error) {
      return errorOrErrorResponse.message;
    }

    if (isOpensearchErrorResponse(errorOrErrorResponse)) {
      return getOpensearchErrorMessage(errorOrErrorResponse.error);
    }

    return undefined;
  };

  searchLogsUsingScrollApi = async <T = any>(
    params: IBuildSearchPayload,
    queryMarkers?: QueryMarkers,
  ): Promise<opensearchtypes.SearchResponse<T>> => {
    const payload = opensearchQueryService.buildSearchPayload(params);
    const query = queryMarkersToString(queryMarkers);
    const path = `${this.urls.scrollSearch}/${params.scroll ? 'scroll_continue' : 'scroll_start'}${query}`;

    return this.crudApiService.do<opensearchtypes.SearchResponse<T>>(path, {
      payload: {
        ...(params.scroll
          ? {
              scroll: params.scroll,
              scrollId: params.scrollId,
            }
          : {
              elasticBody: payload.params.body,
              index: payload.params.index,
              size: payload.params.body!.size,
              sort: (Array.isArray(payload.params.body!.sort) ? payload.params.body!.sort : [payload.params.body!.sort])
                .map(item => `${Object.keys(item ?? {})[0]}:${Object.values(item ?? {})[0]?.order}`)
                .join(','),
            }),
      },
    });
  };
}

export const opensearchApiService = new OpensearchApiService();
